import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  TablePagination,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Select,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import { get, post, put, remove } from "../helpers/apisCalls";
import { Pencil, Trash } from "lucide-react";
import { User } from "../types";
import { useSelector } from "react-redux";
import { RootState } from "../store";

const UsersList: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [addPop, setAddPop] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [formUser, setFormUser] = useState<User>({
    id: "",
    name: "",
    username: "",
    role: "", // Default role is "User"
    password: "", // Default role is "User"
    confirm_password: "", // Default role is "User"
  });
  const [errors, setErrors] = useState<any>({});
  useEffect(() => {
    fetchUsers();
  }, []);
  // Define a response type

  const fetchUsers = async () => {
    try {
      const response: any = await get("/api/users");
      if (response?.users) {
        setUsers(response?.users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  // Handle input changes
  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormUser({
      ...formUser,
      [name]: value,
    });
  };
  const validateForm = (type: string) => {
    const newErrors: any = {};
    if (formUser.name == "") newErrors.name = "Name is required";
    if (formUser.role == "") newErrors.role = "Role is required";
    if (formUser.username == "") newErrors.username = "Username is required";
    if (type == "add") {
      if (!formUser.password) newErrors.password = "Password is required";
    }
    if (formUser.password != "") {
      if (formUser.password !== formUser.confirm_password)
        newErrors.confirm_password = "Passwords do not match";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = async () => {
    try {
      if (formUser.id != "") {
        if (!validateForm("edit")) return;
        const response: any = await put("/api/users", formUser);
      } else {
        if (!validateForm("add")) return;
        const response: any = await post("/api/users", formUser);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      fetchUsers();
      setFormUser({
        id: "",
        name: "",
        username: "",
        password: "",
        role: "",
        confirm_password: "",
      });
      setAddPop(false);
    }
  };
  const deleteUser = async (id: string) => {
    try {
      const response: any = await remove(`/api/users/${id}`);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      fetchUsers();
    }
  };
  if (!user) return <>loading...</>;

  return (
    <Box sx={{ padding: 4 }}>
      <Box display={"flex"} justifyContent={"space-between"} my={2}>
        <Typography variant="h4" gutterBottom>
          Users List
        </Typography>
        <Button
          onClick={() => {
            setAddPop(true);
          }}
        >
          + Add User
        </Button>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S.No.</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users?.map((u, index) => (
                  <TableRow key={u.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.username}</TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell>
                      {user.role === "Admin" && (
                        <Trash
                          style={{ marginRight: "5px", cursor: "pointer" }}
                          onClick={() => u.id && deleteUser(u.id)}
                        />
                      )}
                      <Pencil
                        style={{ marginRight: "5px", cursor: "pointer" }}
                        onClick={() => [setFormUser(u), setAddPop(true)]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={users.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      <Dialog open={addPop} onClose={setAddPop}>
        <DialogTitle>{formUser ? "Edit User" : "Add User"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            name="name"
            value={formUser.name}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            error={!!errors.name}
          />
          <TextField
            label="Username"
            name="username"
            disabled={formUser.id !== "" ? true : false}
            value={formUser.username}
            onChange={handleInputChange}
            fullWidth
            error={!!errors.username}
            margin="normal"
          />
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={formUser.role === "" ? "0" : formUser.role}
            label="Role"
            name="role"
            defaultValue="--Select--"
            onChange={handleInputChange}
            error={!!errors.role}
          >
            <MenuItem value="0">--Select--</MenuItem>
            <MenuItem value="Admin">Admin</MenuItem>
            <MenuItem value="User">User</MenuItem>
          </Select>
          <TextField
            label="Password"
            name="password"
            value={formUser.password}
            error={!!errors.password}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Confirm Password"
            name="confirm_password"
            value={formUser.confirm_password}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            error={!!errors.confirm_password}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPop(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersList;
